import { pluralizeWithCount } from "@common/helpers/string";
import { AccrualImporterGroupBy } from "@common/types/views/accrualImporterViews";
import { ExtractionStatus } from "@common/types/financialDocument";
import { AccrualDocumentImporterMode } from "./accrualDocumentImporter.lib";
import {
    accrualDocumentBeingProcessed,
    getAccrualExtraction,
    getAllAccrualLineItemExtractions,
    resolveAccrualRowExtraction,
} from "./accrualImporter.helpers";
import {
    AccrualImporterDocumentRow,
    AccrualImporterViewRow,
} from "./accrualImporter.types";

const UNASSIGNED_GROUP = "Unassigned";

function keepsSingleStatusRow(
    row: AccrualImporterDocumentRow,
    mode: AccrualDocumentImporterMode,
): boolean {
    const financialDocument = row.financialDocument;
    if (!financialDocument) {
        return true;
    }
    if (accrualDocumentBeingProcessed(financialDocument)) {
        return true;
    }
    const extraction = getAccrualExtraction(financialDocument);
    if (
        !extraction ||
        extraction.status === ExtractionStatus.FAILED ||
        extraction.status !== ExtractionStatus.COMPLETED
    ) {
        return true;
    }
    return (
        getAllAccrualLineItemExtractions(financialDocument, mode).length === 0
    );
}

function viewRow({
    row,
    extractionId,
    lineItemId,
}: {
    row: AccrualImporterDocumentRow;
    extractionId?: string;
    lineItemId?: string;
}): AccrualImporterViewRow {
    const suffix = [extractionId, lineItemId].filter(Boolean).join(":");
    return {
        id: suffix ? `${row.id}:${suffix}` : row.id,
        documentRowId: row.id,
        rowNumber: 0,
        file: row.file,
        financialDocument: row.financialDocument,
        extractionId,
        lineItemId,
    };
}

function extractionRows(
    row: AccrualImporterDocumentRow,
    mode: AccrualDocumentImporterMode,
): AccrualImporterViewRow[] {
    const financialDocument = row.financialDocument;
    if (!financialDocument || keepsSingleStatusRow(row, mode)) {
        return [viewRow({ row })];
    }
    return getAllAccrualLineItemExtractions(financialDocument, mode).map(
        (extraction) => viewRow({ row, extractionId: extraction.id }),
    );
}

function lineItemRows(
    row: AccrualImporterDocumentRow,
    mode: AccrualDocumentImporterMode,
): AccrualImporterViewRow[] {
    const financialDocument = row.financialDocument;
    if (!financialDocument || keepsSingleStatusRow(row, mode)) {
        return [viewRow({ row })];
    }

    const rows = getAllAccrualLineItemExtractions(
        financialDocument,
        mode,
    ).flatMap((extraction) => {
        if (extraction.lineItems.length === 0) {
            return [viewRow({ row, extractionId: extraction.id })];
        }
        return extraction.lineItems.map((lineItem, index) =>
            viewRow({
                row,
                extractionId: extraction.id,
                lineItemId: lineItem.lineId || `index:${index}`,
            }),
        );
    });

    return rows.length > 0 ? rows : [viewRow({ row })];
}

function counterpartyLabel({
    row,
    mode,
    counterpartyNames,
}: {
    row: AccrualImporterViewRow;
    mode: AccrualDocumentImporterMode;
    counterpartyNames: ReadonlyMap<string, string>;
}): string {
    const financialDocument = row.financialDocument;
    if (!financialDocument) {
        return UNASSIGNED_GROUP;
    }
    const extraction = resolveAccrualRowExtraction(
        financialDocument,
        mode,
        row.extractionId,
    );
    const linkedName = extraction?.counterpartyId
        ? counterpartyNames.get(extraction.counterpartyId)
        : undefined;
    const name = (linkedName ?? extraction?.counterpartyName)?.trim();
    if (!name) {
        return UNASSIGNED_GROUP;
    }
    return name;
}

function withRowNumbers(
    rows: AccrualImporterViewRow[],
): AccrualImporterViewRow[] {
    return rows.map((row, index) => ({
        ...row,
        rowNumber: index + 1,
    }));
}

export function buildAccrualDraftGroupedRows({
    rows,
    mode,
    groupBy,
    counterpartyNames,
}: {
    rows: AccrualImporterDocumentRow[];
    mode: AccrualDocumentImporterMode;
    groupBy: AccrualImporterGroupBy;
    counterpartyNames: ReadonlyMap<string, string>;
}): AccrualImporterViewRow[] {
    const expanded = rows.flatMap((row) =>
        groupBy === AccrualImporterGroupBy.LINE_ITEM
            ? lineItemRows(row, mode)
            : extractionRows(row, mode),
    );

    if (groupBy !== AccrualImporterGroupBy.COUNTERPARTY) {
        return withRowNumbers(expanded);
    }

    const groups = new Map<string, AccrualImporterViewRow[]>();
    for (const row of expanded) {
        const label = counterpartyLabel({ row, mode, counterpartyNames });
        const existing = groups.get(label) ?? [];
        existing.push(row);
        groups.set(label, existing);
    }

    const labels = [...groups.keys()].sort((left, right) => {
        if (left === UNASSIGNED_GROUP) {
            return -1;
        }
        if (right === UNASSIGNED_GROUP) {
            return 1;
        }
        return left.localeCompare(right);
    });

    const grouped = labels.flatMap((label) => [
        {
            id: `group:${label}`,
            rowNumber: 0,
            groupLabel: label,
        },
        ...(groups.get(label) ?? []),
    ]);

    return withRowNumbers(grouped);
}

export function documentRowsForDraftSelection({
    selectedRows,
    documentRows,
}: {
    selectedRows: AccrualImporterViewRow[];
    documentRows: AccrualImporterDocumentRow[];
}): AccrualImporterDocumentRow[] {
    const seen = new Set<string>();
    const rows: AccrualImporterDocumentRow[] = [];
    for (const selectedRow of selectedRows) {
        const documentRowId = selectedRow.documentRowId ?? selectedRow.id;
        if (seen.has(documentRowId)) {
            continue;
        }
        seen.add(documentRowId);
        const documentRow = documentRows.find(
            (row) => row.id === documentRowId,
        );
        if (documentRow) {
            rows.push(documentRow);
        }
    }
    return rows;
}

export function omittedExtractionCountForDiscard({
    selectedRows,
    documentRows,
    mode,
}: {
    selectedRows: AccrualImporterViewRow[];
    documentRows: AccrualImporterDocumentRow[];
    mode: AccrualDocumentImporterMode;
}): number {
    const selectedDocumentRows = documentRowsForDraftSelection({
        selectedRows,
        documentRows,
    });
    let omittedCount = 0;
    for (const documentRow of selectedDocumentRows) {
        const financialDocument = documentRow.financialDocument;
        if (!financialDocument) {
            continue;
        }
        const selectedForDocument = selectedRows.filter(
            (row) => (row.documentRowId ?? row.id) === documentRow.id,
        );
        if (selectedForDocument.some((row) => !row.extractionId)) {
            continue;
        }
        const selectedExtractionIds = new Set(
            selectedForDocument.flatMap((row) =>
                row.extractionId ? [row.extractionId] : [],
            ),
        );
        omittedCount += getAllAccrualLineItemExtractions(
            financialDocument,
            mode,
        ).filter(
            (extraction) => !selectedExtractionIds.has(extraction.id),
        ).length;
    }
    return omittedCount;
}

export function discardQuestionForSelection({
    selectedRows,
    documentRows,
    mode,
    itemLabel,
}: {
    selectedRows: AccrualImporterViewRow[] | null;
    documentRows: AccrualImporterDocumentRow[];
    mode: AccrualDocumentImporterMode;
    itemLabel: "invoice" | "bill";
}): string {
    if (!selectedRows) {
        return "This moves the selected files out of Inbox. The files stay in Documents.";
    }
    return discardDraftQuestion({
        fileCount: documentRowsForDraftSelection({
            selectedRows,
            documentRows,
        }).length,
        omittedExtractionCount: omittedExtractionCountForDiscard({
            selectedRows,
            documentRows,
            mode,
        }),
        itemLabel,
    });
}

export function discardDraftQuestion({
    fileCount,
    omittedExtractionCount,
    itemLabel,
}: {
    fileCount: number;
    omittedExtractionCount: number;
    itemLabel: "invoice" | "bill";
}): string {
    if (omittedExtractionCount === 0) {
        return "This moves the selected files out of Inbox. The files stay in Documents.";
    }
    return `This moves ${pluralizeWithCount("file", fileCount)} out of Inbox. ${pluralizeWithCount(itemLabel, omittedExtractionCount)} you did not select will be removed too. The files stay in Documents.`;
}

export function discardedDraftsMessage({
    discardedCount,
    omittedExtractionCount,
    itemLabel,
}: {
    discardedCount: number;
    omittedExtractionCount: number;
    itemLabel: "invoice" | "bill";
}): string {
    const discarded = `Discarded ${pluralizeWithCount("draft", discardedCount)}.`;
    if (omittedExtractionCount === 0) {
        return discarded;
    }
    const verb = omittedExtractionCount === 1 ? "was" : "were";
    return `${discarded} ${pluralizeWithCount(itemLabel, omittedExtractionCount)} you did not select ${verb} removed too.`;
}

export function getAccrualDraftLineItemSiblings({
    rows,
    row,
}: {
    rows: Array<{ id: string; financialDocument?: { id: number } }>;
    row: { id: string; financialDocument?: { id: number } };
}): { hasTopSibling: boolean; hasBottomSibling: boolean } {
    const documentId = row.financialDocument?.id;
    if (documentId == null) {
        return { hasTopSibling: false, hasBottomSibling: false };
    }
    const index = rows.findIndex((item) => item.id === row.id);
    if (index < 0) {
        return { hasTopSibling: false, hasBottomSibling: false };
    }
    return {
        hasTopSibling: rows[index - 1]?.financialDocument?.id === documentId,
        hasBottomSibling: rows[index + 1]?.financialDocument?.id === documentId,
    };
}

export function financialDocumentsForDraftSelection<T extends { id: number }>(
    selectedRows: Array<{ financialDocument?: T }>,
): T[] {
    const seen = new Set<number>();
    const documents: T[] = [];
    for (const selectedRow of selectedRows) {
        const financialDocument = selectedRow.financialDocument;
        if (!financialDocument || seen.has(financialDocument.id)) {
            continue;
        }
        seen.add(financialDocument.id);
        documents.push(financialDocument);
    }
    return documents;
}
